import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery, skipToken } from '@tanstack/react-query'
import { Layout, ProtectedRoute } from '@/components/layout'
import {
  LoginPage,
  SignupPage,
  DashboardPage,
  CanaryListPage,
  CanaryDetailPage,
  CanaryCreatePage,
  DetectionListPage,
  DetectionDetailPage,
  SettingsPage,
  InfrastructurePage,
  UserProfilePage,
  BillingPage,
  AdminDashboardPage,
  AdminOrganizationsPage,
  AdminOrganizationDetailPage,
  AdminUsersPage,
  AdminCanariesPage,
  AdminDetectionsPage,
  AdminAuditLogPage,
  AdminIntegrationErrorsPage,
  AdminAllowlistPage,
  AdminNotificationsPage,
  EmailTemplatesPage,
  OnboardingPage,
} from '@/pages'
import { ImpersonationProvider } from '@/contexts/ImpersonationContext'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { RootErrorFallback } from '@/components/ui/ErrorFallback'
import type { UserProfileResponse } from '@spoofio/shared/types'

function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { data: profile } = useQuery<UserProfileResponse>({
    queryKey: ['user-profile'],
    queryFn: skipToken,
  })

  if (!profile?.isPlatformAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <ImpersonationProvider>
      <ErrorBoundary fallback={(props) => <RootErrorFallback {...props} />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/signup/*" element={<SignupPage />} />

      {/* Onboarding wizard — full viewport, no Layout */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/canaries"
        element={
          <ProtectedRoute>
            <Layout>
              <CanaryListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/canaries/new"
        element={
          <ProtectedRoute>
            <Layout>
              <CanaryCreatePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/canaries/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CanaryDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/detections"
        element={
          <ProtectedRoute>
            <Layout>
              <DetectionListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/detections/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <DetectionDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/infrastructure"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <InfrastructurePage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Layout>
              <BillingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <UserProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/*"
        element={
          <ProtectedRoute>
            <Layout>
              <UserProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Platform Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminDashboardPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminOrganizationsPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/organizations/:id"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminOrganizationDetailPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminUsersPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/canaries"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminCanariesPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/detections"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminDetectionsPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-log"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminAuditLogPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integration-errors"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminIntegrationErrorsPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/allowlist"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminAllowlistPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <AdminNotificationsPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-templates"
        element={
          <ProtectedRoute>
            <PlatformAdminRoute>
              <Layout>
                <EmailTemplatesPage />
              </Layout>
            </PlatformAdminRoute>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </ErrorBoundary>
    </ImpersonationProvider>
  )
}
